//
// Copyright © 2026 ZeroDesk
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/**
 * Date-only values of the personnel file are stored as UTC midnight timestamps
 * so that they do not move with the time zone of the viewer.
 */

/**
 * Value for `<input type="date">` (YYYY-MM-DD) or an empty string.
 * @public
 */
export function dateToInput (value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return ''
  return new Date(value).toISOString().slice(0, 10)
}

/**
 * Parses the value of `<input type="date">` into a UTC midnight timestamp.
 * @public
 */
export function inputToDate (value: string | null | undefined): number | null {
  if (value == null || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const [y, m, d] = value.split('-').map(Number)
  const ts = Date.UTC(y, m - 1, d)
  return Number.isNaN(ts) ? null : ts
}

/**
 * UTC midnight of the given moment.
 * @public
 */
export function todayUtc (now: number = Date.now()): number {
  const d = new Date(now)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

/**
 * Adds calendar months and clamps the day to the length of the target month.
 * @public
 */
export function addMonths (value: number, months: number): number {
  const d = new Date(value)
  const day = d.getUTCDate()
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1))
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
  return Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(day, lastDay))
}

/**
 * Number of full years between two dates.
 * @public
 */
export function fullYearsBetween (from: number, to: number): number {
  const a = new Date(from)
  const b = new Date(to)
  let years = b.getUTCFullYear() - a.getUTCFullYear()
  const monthDiff = b.getUTCMonth() - a.getUTCMonth()
  if (monthDiff < 0 || (monthDiff === 0 && b.getUTCDate() < a.getUTCDate())) years--
  return Math.max(0, years)
}

/**
 * Number of full months between two dates.
 * @public
 */
export function fullMonthsBetween (from: number, to: number): number {
  const a = new Date(from)
  const b = new Date(to)
  let months = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth())
  if (b.getUTCDate() < a.getUTCDate()) months--
  return Math.max(0, months)
}

/**
 * Tenure as `Xy Zm` parts.
 * @public
 */
export function tenure (from: number, to: number = todayUtc()): { years: number, months: number } {
  const total = fullMonthsBetween(from, to)
  return { years: Math.floor(total / 12), months: total % 12 }
}

/**
 * Whole days from `from` until `to` (negative when `to` is earlier).
 * @public
 */
export function daysUntil (to: number, from: number = todayUtc()): number {
  return Math.round((to - from) / 86400000)
}
